import Button from "@components/Button"
import Layout from "@components/Layout"
import { useTranslation } from "react-i18next"
import { Form, useSubmit } from "react-router-dom"

const WAVE_LINE =
  "M 20 95 C 40 95 45 50 65 50 C 85 50 85 85 105 85 C 125 85 125 40 145 40 C 165 40 165 75 185 75 C 205 75 205 30 225 30 C 245 30 245 65 265 65 C 275 65 280 45 280 45"
const WAVE_AREA = `${WAVE_LINE} L 280 140 L 20 140 Z`

const DOTS = [
  { cx: 65, cy: 50 },
  { cx: 105, cy: 85 },
  { cx: 145, cy: 40 },
  { cx: 185, cy: 75 },
  { cx: 225, cy: 30 },
  { cx: 265, cy: 65 },
]

function YoYoChart() {
  return (
    <svg
      viewBox="0 0 300 160"
      className="w-full max-w-xs"
      preserveAspectRatio="xMidYMid meet"
    >
      <defs>
        <linearGradient id="yoyoArea" x1="0" x2="0" y1="0" y2="1">
          <stop offset="0%" stopColor="#ff8282" stopOpacity="0.15" />
          <stop offset="100%" stopColor="#ff8282" stopOpacity="0.01" />
        </linearGradient>
      </defs>

      {/* Grid lines */}
      {[40, 70, 100, 130].map((y) => (
        <line
          key={y}
          x1="20"
          y1={y}
          x2="280"
          y2={y}
          stroke="#f3f4f6"
          strokeWidth="1"
        />
      ))}

      {/* Area fill */}
      <path d={WAVE_AREA} fill="url(#yoyoArea)">
        <animate attributeName="opacity" values="0;1" dur="1s" fill="freeze" />
      </path>

      {/* Smooth wave line */}
      <path
        d={WAVE_LINE}
        fill="none"
        stroke="#ff8282"
        strokeWidth="2.5"
        strokeLinecap="round"
        strokeDasharray="600"
        strokeDashoffset="600"
      >
        <animate
          attributeName="stroke-dashoffset"
          from="600"
          to="0"
          dur="1.8s"
          fill="freeze"
          calcMode="spline"
          keySplines="0.4 0 0.2 1"
          keyTimes="0;1"
        />
      </path>

      {/* Goal line */}
      <line
        x1="20"
        y1="105"
        x2="280"
        y2="105"
        stroke="#7AF2A8"
        strokeWidth="1.5"
        strokeDasharray="6 4"
        opacity="0.5"
      />

      {/* Dots at peaks and valleys */}
      {DOTS.map((dot, i) => (
        <circle
          key={i}
          cx={dot.cx}
          cy={dot.cy}
          r="4"
          fill="white"
          stroke="#ff8282"
          strokeWidth="2"
          opacity="0"
        >
          <animate
            attributeName="opacity"
            from="0"
            to="1"
            dur="0.3s"
            begin={`${0.3 * (i + 1)}s`}
            fill="freeze"
          />
        </circle>
      ))}

      {/* Labels */}
      <text
        x="280"
        y="118"
        textAnchor="end"
        className="fill-custom-teal text-[9px] font-semibold"
      >
        Goal
      </text>
      <text
        x="20"
        y="152"
        textAnchor="start"
        className="fill-gray-400 text-[9px]"
      >
        Time →
      </text>
    </svg>
  )
}

export default function RestrictiveDiets() {
  const { t } = useTranslation()
  const submit = useSubmit()

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    submit(event.currentTarget)
  }

  return (
    <Layout>
      <div className="flex grow flex-col items-center justify-center px-4">
        <div className="mb-6">
          <YoYoChart />
        </div>

        <h1 className="custom-heading mb-4">
          {t("pages.onboarding.restrictiveDiets.heading")}
        </h1>

        <p className="mb-6 max-w-sm text-center leading-relaxed text-gray-500">
          {t("pages.onboarding.restrictiveDiets.info")}
        </p>
      </div>

      <div className="sticky bottom-0 -mx-5 bg-gradient-to-t from-white from-70% to-transparent px-5 pt-8 pb-4">
        <Form method="post" onSubmit={handleSubmit}>
          <input name="restrictiveDiets" type="hidden" value="continue" />
          <Button title={t("shared.continue")} type="submit" />
        </Form>
      </div>
    </Layout>
  )
}
